import { buildXlsx, XCell, XS, XSheet } from '../event-report/event-report-xlsx';
import { REPORT_BRAND, formatDateTime, formatMoney, pad2 } from '../event-report/event-report.model';
import { IMPORT_FIELDS, TEMPLATE_HEADERS } from './product-import.model';

/**
 * Exportación de los productos de una zona de evento en Excel, CSV y PDF,
 * más la plantilla de importación. Las tres salidas llevan las mismas
 * columnas que entiende la importación, así un archivo exportado se puede
 * corregir y volver a subir sin tocar encabezados. Funciones puras.
 */

export interface ProductExportRow {
    name: string;
    code: string;
    supplier: string;
    price: number;
    /** Precio antes del descuento, si lo hay. */
    priceUndiscount: number | null;
    inventory: number;
    zone: string;
    /** Pestaña de la caja (Botellas, Tragos…). */
    section: string;
    visible: boolean;
    /** Producto referenciado desde otra categoría. */
    reference: boolean;
}

export interface ProductExportMeta {
    eventName: string;
    zoneName: string;
    currencyCode: string;
    currencySymbol: string;
    generatedAt: Date;
    generatedBy: string;
}

export function exportFileName(meta: ProductExportMeta, ext: string): string {
    const slug = String(meta.zoneName || meta.eventName || 'Zona')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60) || 'Zona';
    const d = meta.generatedAt;
    const stamp = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    return `Productos_${slug}_${stamp}.${ext}`;
}

// ═══════════════════════════════════════════════════════════════════════════
//  EXCEL
// ═══════════════════════════════════════════════════════════════════════════

const text = (v: string, s: number = XS.normal): XCell => ({ v, s });
const money = (v: number): XCell => ({ v: Number(v || 0), s: XS.money });
const int = (v: number): XCell => ({ v: Number(v || 0), s: XS.int });

/** Anchos de la tabla de productos, en caracteres, en el orden de TEMPLATE_HEADERS. */
const PRODUCT_COLS = [38, 14, 22, 12, 12, 24, 18, 10];

function productRow(row: ProductExportRow): XCell[] {
    return [
        text(row.name),
        text(row.code || ''),
        text(row.supplier || ''),
        money(row.price),
        int(row.inventory),
        text(row.zone || ''),
        text(row.section || ''),
        text(row.visible ? 'Sí' : 'No'),
    ];
}

export function buildProductsXlsx(rows: ProductExportRow[], meta: ProductExportMeta): Uint8Array {
    const head = TEMPLATE_HEADERS.map(h => text(h, XS.header));
    const sheet: XSheet = {
        name: 'Productos',
        cols: PRODUCT_COLS,
        freezeRow: 1,
        rows: [head, ...rows.map(productRow)],
    };

    const info: XSheet = {
        name: 'Información',
        cols: [22, 60],
        rows: [
            [text('Productos de la zona', XS.title)],
            [],
            [text('Evento', XS.bold), text(meta.eventName || '')],
            [text('Zona', XS.bold), text(meta.zoneName || '')],
            [text('Moneda', XS.bold), text(meta.currencyCode || '')],
            [text('Productos', XS.bold), int(rows.length)],
            [text('Generado el', XS.bold), text(formatDateTime(meta.generatedAt))],
            [text('Generado por', XS.bold), text(meta.generatedBy || '')],
            [],
            [text('Este archivo se puede corregir y volver a importar desde la zona: se respetan los mismos encabezados.', XS.muted)],
        ],
    };

    return buildXlsx([sheet, info], meta.generatedAt);
}

/**
 * Plantilla para llenar a mano: encabezados, dos filas de ejemplo y una
 * hoja aparte con las zonas válidas del evento para copiar el nombre exacto.
 */
export function buildTemplateXlsx(zones: string[], currentZone: string): Uint8Array {
    const head = TEMPLATE_HEADERS.map(h => text(h, XS.header));
    const zone = currentZone || (zones.length ? zones[0] : '');
    const products: XSheet = {
        name: 'Productos',
        cols: PRODUCT_COLS,
        freezeRow: 1,
        rows: [
            head,
            [text('Cerveza nacional 355 ml'), text('CERV-355'), text('Cervecería'), money(3.5), int(120), text(zone), text('Cervezas'), text('Sí')],
            [text('Coctel de la casa'), text(''), text(''), money(8), int(40), text(zone), text('Cócteles'), text('Sí')],
        ],
    };

    const help: XSheet = {
        name: 'Instrucciones',
        cols: [16, 70],
        rows: [
            [text('Cómo llenar la plantilla', XS.title)],
            [],
            [text('Columna', XS.header), text('Qué va', XS.header)],
            ...IMPORT_FIELDS.filter(f => f.key !== 'ignore').map(f => [text(f.label, XS.bold), text(f.hint + (f.required ? ' (obligatorio)' : ''))]),
            [],
            [text('Ubicación', XS.bold), text('Si se deja vacía, el producto se crea en la zona desde la que se importa. Una ubicación que no exista se crea como categoría dentro de esa zona; con "/" se anidan: "Precio Regular / Botellas". Zonas válidas:')],
            ...zones.map(z => [text(''), text(z)]),
            [],
            [text('Sección', XS.bold), text('Pestaña con la que la caja agrupa los productos (Botellas, Tragos, Cervezas, Sin alcohol…). Se crea sola si no existe; vacía = sin sección.')],
            [],
            [text('Si un producto ya existe en la zona con el mismo nombre, se actualizan su precio, inventario, código y proveedor en lugar de duplicarlo.', XS.muted)],
        ],
    };

    return buildXlsx([products, help]);
}

// ═══════════════════════════════════════════════════════════════════════════
//  CSV
// ═══════════════════════════════════════════════════════════════════════════

function csvCell(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return isFinite(value) ? String(Math.round(value * 100) / 100) : '';
    const str = String(value);
    return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/** Con BOM para que Excel abra las tildes bien; montos como número plano. */
export function buildProductsCsv(rows: ProductExportRow[]): string {
    const lines = [TEMPLATE_HEADERS.map(csvCell).join(',')];
    rows.forEach(row => {
        lines.push([
            row.name, row.code || '', row.supplier || '', row.price, row.inventory, row.zone || '', row.section || '', row.visible ? 'Sí' : 'No',
        ].map(csvCell).join(','));
    });
    return '﻿' + lines.join('\r\n');
}

// ═══════════════════════════════════════════════════════════════════════════
//  PDF (lista de precios de la zona)
// ═══════════════════════════════════════════════════════════════════════════

export function buildProductsPdfDoc(rows: ProductExportRow[], meta: ProductExportMeta, logoDataUri: string): any {
    const B = REPORT_BRAND;
    const currency = (n: number) => formatMoney(n, meta.currencySymbol);
    const generated = formatDateTime(meta.generatedAt);
    const year = meta.generatedAt.getFullYear();

    const header = () => ({
        margin: [40, 26, 40, 0],
        stack: [
            {
                columns: [
                    { image: 'logo', width: 104, margin: [0, 2, 0, 0] },
                    {
                        width: '*',
                        alignment: 'right',
                        stack: [
                            { text: 'PRODUCTOS DE LA ZONA', style: 'eyebrow' },
                            { text: meta.zoneName || 'Zona', style: 'headTitle' },
                        ],
                    },
                ],
            },
            {
                canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.2, lineColor: B.wine }],
                margin: [0, 8, 0, 0],
            },
        ],
    });

    const footer = (currentPage: number, pageCount: number) => ({
        margin: [40, 12, 40, 0],
        stack: [
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.6, lineColor: B.line }] },
            {
                margin: [0, 7, 0, 0],
                columns: [
                    { width: '*', text: `${B.company} · Desarrollado por ${B.tech} © ${year}`, style: 'foot' },
                    { width: '*', alignment: 'center', text: meta.eventName ? `Evento: ${meta.eventName}` : '', style: 'foot' },
                    { width: '*', alignment: 'right', text: `Generado el ${generated} · Página ${currentPage} de ${pageCount}`, style: 'foot' },
                ],
            },
        ],
    });

    const layout = {
        hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0.8 : 0.4),
        vLineWidth: () => 0,
        hLineColor: (i: number, node: any) => (i === 0 || i === 1 || i === node.table.body.length ? B.wine : B.line),
        fillColor: (rowIndex: number) => (rowIndex === 0 ? B.wine : (rowIndex % 2 === 0 ? B.zebra : null)),
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 5,
        paddingBottom: () => 5,
    };

    const head = [
        { text: '#', style: 'th', alignment: 'right' },
        { text: 'Producto', style: 'th' },
        { text: 'Código', style: 'th' },
        { text: 'Proveedor', style: 'th' },
        { text: 'Precio', style: 'th', alignment: 'right' },
        { text: 'Inventario', style: 'th', alignment: 'right' },
        { text: 'Visible', style: 'th', alignment: 'center' },
    ];
    const body = rows.map((row, i) => [
        { text: String(i + 1), style: 'tdMuted', alignment: 'right' },
        {
            stack: [
                { text: row.name, style: 'td' },
                // Zona y sección debajo del nombre: la lista de precios se lee por pestaña.
                (row.zone || row.section) ? { text: [row.zone, row.section].filter(x => !!x).join(' · '), style: 'tdMuted' } : null,
            ].filter(x => !!x),
        },
        { text: row.code || '—', style: row.code ? 'td' : 'tdMuted' },
        { text: row.supplier || '—', style: row.supplier ? 'td' : 'tdMuted' },
        {
            stack: [
                row.priceUndiscount ? { text: currency(row.priceUndiscount), style: 'tdStrike', alignment: 'right' } : null,
                { text: currency(row.price), style: 'tdBold', alignment: 'right' },
            ].filter(x => !!x),
        },
        { text: String(row.inventory), style: row.inventory > 0 ? 'td' : 'tdWarn', alignment: 'right' },
        { text: row.visible ? 'Sí' : 'No', style: row.visible ? 'td' : 'tdMuted', alignment: 'center' },
    ]);

    const visibles = rows.filter(r => r.visible).length;
    const units = rows.reduce((acc, r) => acc + (Number(r.inventory) || 0), 0);

    const kpi = (label: string, value: string) => ({
        width: '*',
        stack: [{ text: label, style: 'kpiLabel' }, { text: value, style: 'kpiValue' }],
    });

    return {
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [40, 92, 40, 66],
        info: {
            title: `Productos – ${meta.zoneName || 'Zona'}`,
            author: B.company,
            subject: 'Lista de productos de la zona del evento',
            keywords: 'barlleno, evento, productos, precios',
            creator: `${B.company} · ${B.tech}`,
        },
        images: { logo: logoDataUri },
        header,
        footer,
        content: [
            { text: 'Lista de productos y precios', style: 'docTitle' },
            {
                text: `Productos configurados en ${meta.zoneName || 'la zona'}${meta.eventName ? ` de ${meta.eventName}` : ''}, con su precio, inventario y visibilidad en la app al momento de generar el documento.`,
                style: 'docSub',
            },
            {
                margin: [0, 0, 0, 16],
                table: {
                    widths: ['*', '*', '*', '*'],
                    body: [[
                        kpi('PRODUCTOS', String(rows.length)),
                        kpi('VISIBLES EN LA APP', String(visibles)),
                        kpi('UNIDADES EN INVENTARIO', String(units)),
                        kpi('MONEDA', meta.currencyCode || ''),
                    ]],
                },
                layout: {
                    hLineWidth: () => 0.8,
                    vLineWidth: () => 0,
                    hLineColor: () => B.wine,
                    fillColor: () => B.zebra,
                    paddingLeft: () => 10,
                    paddingRight: () => 10,
                    paddingTop: () => 8,
                    paddingBottom: () => 8,
                },
            },
            rows.length > 0
                ? {
                    table: { headerRows: 1, dontBreakRows: true, widths: [18, '*', 58, 78, 58, 52, 42], body: [head, ...body] },
                    layout,
                }
                : { text: 'La zona no tiene productos.', style: 'empty' },
            {
                text: `Documento generado automáticamente por ${B.company} el ${generated}${meta.generatedBy ? ` por ${meta.generatedBy}` : ''}.`,
                style: 'closing',
                margin: [0, 22, 0, 0],
            },
        ],
        styles: {
            eyebrow: { fontSize: 7.5, bold: true, color: B.wine, characterSpacing: 1.2 },
            headTitle: { fontSize: 12.5, bold: true, color: B.ink, margin: [0, 2, 0, 0] },
            foot: { fontSize: 7.5, color: B.muted },
            docTitle: { fontSize: 20, bold: true, color: B.ink, margin: [0, 0, 0, 4] },
            docSub: { fontSize: 9.5, color: B.muted, margin: [0, 0, 0, 14] },
            kpiLabel: { fontSize: 7, bold: true, color: B.muted, characterSpacing: 0.8 },
            kpiValue: { fontSize: 15, bold: true, color: B.wine, margin: [0, 3, 0, 0] },
            th: { fontSize: 7.8, bold: true, color: '#ffffff' },
            td: { fontSize: 8.6, color: B.ink },
            tdBold: { fontSize: 8.6, bold: true, color: B.ink },
            tdMuted: { fontSize: 7.8, color: B.muted },
            tdStrike: { fontSize: 7.4, color: B.muted, decoration: 'lineThrough' },
            tdWarn: { fontSize: 8.6, bold: true, color: '#b45309' },
            empty: { fontSize: 9, italics: true, color: B.muted },
            closing: { fontSize: 8, italics: true, color: B.muted },
        },
        defaultStyle: { fontSize: 9, color: B.ink, lineHeight: 1.15 },
    };
}
